import { useTranslation } from 'react-i18next';

import AlarmWarningLine from '@/assets/icons/ri_alarm_warning_line.svg?component';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import Tooltip from '@/components/atoms/Tooltip';
import { formatNumberByLocale, parseLocale } from '@/utils/localeFormatters';

/**
 * Fish counted, warning when there were too few for the average to mean much.
 *
 * Lived inside `LiceReportLocationTable`'s render body, which meant React saw a **new component
 * type on every render** and threw away the whole subtree each time. Out here it is one type,
 * declared once.
 *
 * The thresholds differ by mode because the periods do: a single day and a whole week are not
 * comparable sample sizes.
 */
const THRESHOLD = { daily: 100, weekly: 50 } as const;

interface CountValueProps {
    /** Already formatted, or empty when there is nothing to show. */
    value: string;
    alarm: boolean;
}

function CountValue({ value, alarm }: CountValueProps) {
    if (!alarm) {
        return <span>{value}</span>;
    }

    return (
        <span data-type="alarm">
            {value && <span>{value}</span>}
            <SvgIcon icon={AlarmWarningLine} size="inherit" />
        </span>
    );
}

export interface CountCellProps {
    /** Absent when the pen reported nothing at all that period. */
    count: number | undefined;
    mode: 'weekly' | 'daily';
}

export default function CountCell({ count, mode }: CountCellProps) {
    const { t, i18n } = useTranslation();
    const { t: tLiceReport } = useTranslation('liceReport');
    const locale = parseLocale(i18n.language);

    // Three states, and the first two are not orderable against a number. The previous version
    // tested `count < threshold` first, which is `false` for `undefined` — so the "no data"
    // tooltip it prepared was never reachable and the cell rendered a formatted `undefined`. It
    // also nested a `count > threshold` branch inside the `count < threshold` one, which could
    // never be taken.
    const warning = warningFor(count, mode, t, tLiceReport);

    // Tooltip renders the bare value when `content` is undefined, which is exactly the "enough
    // fish, nothing to say" case, so the cell has one return instead of three. `inline`, because
    // `fit` wraps the target in a block that the cell's `text-align: center` does not move.
    return (
        <Tooltip content={warning} layout="inline">
            <CountValue
                value={count === undefined ? '' : formatNumberByLocale(count, locale, 0)}
                alarm={warning !== undefined}
            />
        </Tooltip>
    );
}

type Translate = (key: string) => string;

/** The message to warn with, or undefined when the sample speaks for itself. */
function warningFor(
    count: number | undefined,
    mode: 'weekly' | 'daily',
    t: Translate,
    tLiceReport: Translate
): string | undefined {
    if (count === undefined) {
        return t('common.labels.noDataFound');
    }

    if (count < THRESHOLD[mode]) {
        return mode === 'daily'
            ? tLiceReport('table.lessThan100Counts')
            : t('organisms.liceReportLocationTable.lessThan50Counts');
    }

    return undefined;
}
